// Shared geometry for the Portfolio Preview card deck. Both decks (ArrowDeck on
// desktop, SwipeDeck on touch) render the same stack and differ only in what
// drives `p` — the arrows vs. a finger. Keeping the numbers here means the
// two can never drift apart visually.
//
// Scale comes from the Figma frame (1440×919): cards are 1440 / 1343 / 1245
// wide front-to-back, a constant ~6.75% step per depth level.
export const SCALE_STEP = 0.0675;

// Lift per depth level, as a share of the card's own height. Expressed as a
// percentage rather than pixels because the deck renders at wildly different
// sizes — full-bleed on a desktop, a ~440px card on a phone — and a fixed pixel
// lift that reads as a subtle stagger on one would swamp the other.
//
// Careful when re-deriving this from a design file: cards scale about their
// CENTRE, so this is the gap between card CENTRES, not between their top edges.
// Shrinking a card already pushes its top edge DOWN by half the height it lost
// (~3.4% here), so the lift has to clear that before any of the card behind
// becomes visible at all. Anything under ~3.4% and the stack looks like a single
// card — the ones behind are hidden exactly beneath the front one.
export const Y_STEP_PERCENT = 10;

// How a card leaves once it has been pushed off the front. The whole point is
// that it should read as being SWAPPED OUT — a card physically leaving while the
// next one takes its place — rather than dissolving where it stands. Three
// things carry that, and getting any of them wrong turns it back into a fade:
//
//   EXIT_SPAN        how long the exit takes, in depth units. Must be just under
//                    1 so it finishes in step with the card arriving from
//                    behind; the pair then reads as one exchange. It also has to
//                    stay BELOW 1, or the card wrapping round to the back has no
//                    room left to fade in and pops into the stack instead.
//   EXIT_Y_PERCENT   far enough to clear the frame entirely. A card that stops
//                    while still overlapping the deck has to be faded away, and
//                    a fade is exactly what we are avoiding.
//   EXIT_FADE_START  how much of that travel happens at FULL opacity. This is
//                    the one that matters most: fading from the first frame
//                    means the card is half gone before it has visibly moved, so
//                    the eye reads "fading" and never sees the movement. Holding
//                    it opaque through the first half means the eye tracks the
//                    card leaving, and the fade only tidies up the last moment.
//
// EXIT_SCALE is deliberately 0 — the card falls out of the deck rather than
// lunging toward the viewer. Raise it if the exit should grow on the way out.
export const EXIT_SPAN = 0.8;
export const EXIT_SCALE = 0;
export const EXIT_Y_PERCENT = 100;
export const EXIT_FADE_START = 0.55;

// The fall accelerates instead of running at a constant rate — the card hangs in
// the deck for a beat, then drops away. Linear travel is what makes a swap feel
// mechanical.
export const EXIT_EASE = 1.7;

// Spring applied to the raw scroll progress. Carried over from the previous
// pinned showcase — the deliberate lag behind the scroll ("หน่วง") was a
// requested feel. Overdamped so it eases in without bouncing.
export const SPRING = {
  stiffness: 70,
  damping: 22,
  mass: 0.5,
  restDelta: 0.0005,
};

/**
 * Depth of card `index` given deck position `p`, wrapped so the deck never
 * empties out: as the front card is pushed away, the card that just left
 * reappears at the back of the stack.
 *
 * The `+ EXIT_SPAN … - EXIT_SPAN` shift is what hides the wrap. A plain modulo
 * would send the front card straight to the back the instant it started moving.
 * Shifting the window lets the result go NEGATIVE down to -EXIT_SPAN first —
 * that negative stretch is the card punching out toward the viewer — and only
 * then does it wrap round to the deepest position. Because the card's opacity is
 * already 0 at both ends of that jump, the seam is never visible.
 *
 * @returns depth in [-EXIT_SPAN, count - EXIT_SPAN); 0 is the front card.
 */
export function depthOf(index, p, count) {
  const shifted = index - p + EXIT_SPAN;
  return (((shifted % count) + count) % count) - EXIT_SPAN;
}

// Cards grow past 1 while exiting, then shrink a step per depth level behind.
export function depthScale(depth) {
  if (depth < 0) return 1 + (-depth / EXIT_SPAN) * EXIT_SCALE;
  return 1 - depth * SCALE_STEP;
}

// Behind the front card the stack rides upward; the exiting card drops instead,
// which reads as "coming past you" rather than "sinking back". In percent of the
// card's own height — see Y_STEP_PERCENT.
export function depthYPercent(depth) {
  if (depth < 0) {
    const t = -depth / EXIT_SPAN;
    return EXIT_Y_PERCENT * Math.pow(t, EXIT_EASE);
  }
  return -depth * Y_STEP_PERCENT;
}

// Visible across the whole resting stack; fades only at the two ends of the
// wrap, so the jump from front-exit to back-of-stack happens out of sight.
export function depthOpacity(depth, count) {
  if (depth < 0) {
    // Fully opaque for the first stretch of the exit, so the movement is what
    // the eye follows; the fade only covers the tail end.
    const t = -depth / EXIT_SPAN;
    if (t <= EXIT_FADE_START) return 1;
    return 1 - (t - EXIT_FADE_START) / (1 - EXIT_FADE_START);
  }

  // Coming back in at the back of the stack. The window is whatever depth is
  // left over once the exit has taken its share (1 - EXIT_SPAN), which is why
  // EXIT_SPAN has to stay under 1.
  const deepest = count - 1;
  if (depth > deepest) return (count - EXIT_SPAN - depth) / (1 - EXIT_SPAN);
  return 1;
}

// Monotonically decreasing in depth, so the exiting card sits above the front
// one and the stack layers in the right order. Integer — it is a real z-index.
export function depthZ(depth) {
  return Math.round(100 - depth * 10);
}

// A touch of shade on the cards further back. Cheap depth cue that keeps the
// stack readable when several product shots have similar brightness.
export function depthShade(depth) {
  if (depth <= 0) return 0;
  return Math.min(depth * 0.12, 0.3);
}
